import {
  parseProviderAuthConfig,
  ProviderAuthorizationError,
  type ProviderSessionProjection,
  type ProviderSlotSummary,
} from "@/lib/providerAuth";
import {
  ProviderAuthClient,
  ProviderRequestError,
  ProviderSessionRejectedError,
} from "@/lib/providerAuthClient";
import type { User } from "@/stores/auth";
import { BrowserProviderRefreshCoordinator } from "@/lib/providerRefreshCoordinator";
import { buildSupportProjection, safeSupportReturn } from "./providerSupportBootstrap";
import { supportConfig } from "./supportConfig";
import {
  clearProviderSessionRestore,
  consumeProviderSessionRestoreReturnTo,
  currentProviderLogoutEpoch,
  invalidateProviderSessionRestores,
  markProviderSessionRestore,
  providerSessionRestoreMatchesLogoutEpoch,
  providerSessionRestorePending,
} from "@/lib/providerSessionRestore";

const providerEnvironment = import.meta.env as Record<string, string | undefined>;
const providerAuthRequested =
  (providerEnvironment.VITE_IDENTITY_PROVIDER_MODE ?? "legacy") === "provider";

export const providerAuthConfig = (() => {
  try {
    return parseProviderAuthConfig(providerEnvironment);
  } catch {
    return null;
  }
})();

let browserClient: ProviderAuthClient | null = null;
const supportReturnKey = "wenlix-admin-support-return-v1";
function rememberSupportReturn(value: string) {
  const path = safeSupportReturn(value);
  if (path) window.sessionStorage.setItem(supportReturnKey, path);
  else window.sessionStorage.removeItem(supportReturnKey);
}
export function consumeSupportSignInReturn(): string | null {
  const stored = window.sessionStorage.getItem(supportReturnKey);
  window.sessionStorage.removeItem(supportReturnKey);
  return stored ? safeSupportReturn(stored) : null;
}

export function getProviderAuthClient(): ProviderAuthClient | null {
  if (!providerAuthConfig || typeof window === "undefined") return null;
  browserClient ??= new ProviderAuthClient(
    providerAuthConfig,
    window.sessionStorage,
    window.fetch.bind(window),
    new BrowserProviderRefreshCoordinator(providerAuthConfig.clientId),
    {
      enabled: () => /^\/support(?:\/|$)/.test(window.location.pathname) || Boolean(safeSupportReturn(window.sessionStorage.getItem(supportReturnKey) ?? "")),
      load: async (accessToken, subject) => {
        const response = await window.fetch(`${supportConfig.apiBase}/session/me`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${accessToken}` },
          cache: "no-store", credentials: "omit", redirect: "error",
          signal: AbortSignal.timeout(10000),
        });
        if (!response.ok) throw new ProviderRequestError("support session bootstrap", response.status);
        return buildSupportProjection(await response.json(), subject, providerAuthConfig!);
      },
    },
  );
  return browserClient;
}

export function isProviderAuthEnabled(): boolean {
  return providerAuthConfig !== null;
}

export function isProviderAuthRequested(): boolean {
  return providerAuthRequested;
}

export function isProviderSessionRejected(error: unknown): boolean {
  return error instanceof ProviderRequestError || error instanceof ProviderSessionRejectedError;
}

export function providerSessionFailureAccountUuid(error: unknown): string | null {
  return error instanceof ProviderSessionRejectedError ? error.accountUuid : null;
}

export function providerProjectionToUser(projection: ProviderSessionProjection): User {
  return {
    id: projection.accountUuid,
    email: projection.profile.email,
    first_name: projection.profile.firstName || undefined,
    last_name: projection.profile.lastName || undefined,
    full_name: projection.profile.displayName || undefined,
    role: projection.uiRole,
    auth_source: "provider",
    account_uuid: projection.accountUuid,
    account_type: projection.accountType,
    provider_subject: projection.subject,
    organization_id: projection.organizationId,
    studio_id: projection.studioId,
    business_role: projection.businessRole,
    permissions: [...projection.permissions],
    business_api: projection.businessApi,
    support_only: projection.supportOnly === true,
  };
}

export async function beginProviderSignIn(loginHint?: string): Promise<void> {
  const client = getProviderAuthClient();
  if (!client) throw new Error("provider sign-in is disabled");
  clearProviderSessionRestore(window.sessionStorage);
  rememberSupportReturn(new URLSearchParams(window.location.search).get("returnTo") ?? "");
  window.location.assign(await client.begin(Date.now(), loginHint));
}

export async function beginProviderSessionRestore(
  returnTo: string,
  expectedAccountUuid: string,
): Promise<void> {
  const client = getProviderAuthClient();
  if (!client) throw new Error("provider sign-in is disabled");
  const restoreEpoch = currentProviderLogoutEpoch(window.localStorage);
  rememberSupportReturn(returnTo);
  const authorizationUrl = await client.beginSessionRestore(expectedAccountUuid);
  markProviderSessionRestore(
    window.sessionStorage,
    returnTo,
    restoreEpoch,
  );
  try {
    window.location.assign(authorizationUrl);
  } catch (error) {
    clearProviderSessionRestore(window.sessionStorage);
    throw error;
  }
}

export function isProviderSessionRestorePending(): boolean {
  return typeof window !== "undefined" && providerSessionRestorePending(window.sessionStorage);
}

export function isProviderSessionRestoreCurrent(): boolean {
  return typeof window !== "undefined" && providerSessionRestoreMatchesLogoutEpoch(
    window.sessionStorage,
    window.localStorage,
  );
}

export function consumeProviderSessionRestoreReturnPath(): string {
  if (typeof window === "undefined") return "/dashboard";
  return consumeProviderSessionRestoreReturnTo(window.sessionStorage);
}

export function clearProviderSessionRestoreAttempt(): void {
  if (typeof window !== "undefined") {
    clearProviderSessionRestore(window.sessionStorage);
  }
}

export function invalidateProviderSessionRestoreEpoch(): void {
  if (typeof window !== "undefined") {
    clearProviderSessionRestore(window.sessionStorage);
    try {
      invalidateProviderSessionRestores(window.localStorage);
    } catch {
      // Revocation and local logout must still proceed if shared storage is unavailable.
    }
  }
}

export function providerSignInErrorMessage(error: unknown): string {
  if (error instanceof ProviderAuthorizationError) {
    const code = error.code.toLowerCase();
    const description = error.description?.toLowerCase() ?? "";
    const combined = `${code} ${description}`;
    const invalid = ["invalid", "incorrect", "wrong", "expired", "устар", "недейств", "неверн", "неправ"]
      .some((marker) => combined.includes(marker));
    const mentionsCode = ["code", "код", "verification", "reset"]
      .some((marker) => combined.includes(marker));
    if (mentionsCode && invalid) {
      return "Код недействителен или устарел. Если вы запрашивали его повторно, используйте код из последнего письма.";
    }
    if ((combined.includes("password") || combined.includes("парол") || combined.includes("credential")) && invalid) {
      return "Неверный email или пароль.";
    }
    if (code === "access_denied") return "Вход отменён. Нажмите «Войти» ещё раз.";
    if (code === "login_required" || code === "interaction_required") {
      return "Войдите в Wenlix ID в открывшемся окне.";
    }
    if (code === "invalid_grant") return "Сессия входа устарела. Нажмите «Войти» ещё раз.";
    if (code === "server_error" || code === "temporarily_unavailable") {
      return "Wenlix ID временно недоступен. Попробуйте позже.";
    }
  }
  if (error instanceof ProviderRequestError) {
    if (error.status === 401) return "Неверный email или пароль.";
    if (error.status === 403) return "Для этого аккаунта вход через Wenlix ID недоступен.";
    if (error.status >= 500) return "Wenlix ID временно недоступен. Попробуйте позже.";
  }
  return "Не удалось завершить вход. Попробуйте войти ещё раз.";
}

export async function completeProviderSignIn(callbackUrl: string): Promise<User> {
  const client = getProviderAuthClient();
  if (!client) throw new Error("provider sign-in is disabled");
  return providerProjectionToUser(await client.complete(callbackUrl));
}

export function activeProviderAccessToken(): string | null {
  return getProviderAuthClient()?.activeTokens()?.accessToken ?? null;
}

export function activeProviderAccountUuid(): string | null {
  return getProviderAuthClient()?.activeAccountUuid() ?? null;
}

export function hasActiveProviderTokens(): boolean {
  const tokens = getProviderAuthClient()?.activeTokens();
  return Boolean(tokens?.accessToken && tokens.refreshToken);
}

export async function refreshActiveProviderSession(): Promise<string> {
  const client = getProviderAuthClient();
  if (!client) throw new Error("provider sign-in is disabled");
  return (await client.refreshActive()).accessToken;
}

export async function logoutActiveProviderSession(): Promise<void> {
  invalidateProviderSessionRestoreEpoch();
  await getProviderAuthClient()?.logoutActive();
}

export async function revokeActiveProviderSessionAfterPeerLogout(): Promise<void> {
  await getProviderAuthClient()?.logoutActive();
}

export function discardActiveProviderSession(): void {
  const client = getProviderAuthClient();
  client?.discardActive();
}

export function discardProviderSession(accountUuid: string): void {
  getProviderAuthClient()?.discard(accountUuid);
}

export function deactivateProviderSession(): void {
  getProviderAuthClient()?.deactivate();
}

export function listProviderSlots(): ProviderSlotSummary[] {
  return getProviderAuthClient()?.listSlots() ?? [];
}

export async function activateProviderSlot(accountUuid: string): Promise<User> {
  const client = getProviderAuthClient();
  if (!client) throw new Error("provider sign-in is disabled");
  return providerProjectionToUser(await client.activate(accountUuid));
}

export async function reloadActiveProviderUser(): Promise<User> {
  const client = getProviderAuthClient();
  if (!client) throw new Error("provider slot is not active");
  return providerProjectionToUser(await client.reloadActiveSession());
}
